interface ErrorBody {
  title?: string;
  detail?: string;
  reason?: string;
  errorCode?: string;
  kind?: string;
  errors?: Record<string, string[]>;
}

/** A failed API call. `reason` is the server's own words, for `Banner reason`. */
export class ApiError extends Error {
  readonly status: number;
  readonly title?: string;
  readonly detail?: string;
  readonly reason?: string;
  readonly errorCode?: string;
  /** Which refusal, where the endpoint names one. */
  readonly kind?: string;
  readonly fieldErrors: Record<string, string[]>;

  constructor(status: number, body: ErrorBody | null | undefined, message?: string) {
    const b = body && typeof body === 'object' ? body : {};
    const reason = b.reason ?? b.detail ?? undefined;
    super(message ?? reason ?? b.title ?? `Request failed (HTTP ${status})`);
    this.name = 'ApiError';
    this.status = status;
    this.title = b.title;
    this.detail = b.detail;
    this.reason = reason;
    this.errorCode = b.errorCode;
    this.kind = b.kind;
    this.fieldErrors = b.errors ?? {};
  }

  static network(cause: unknown): ApiError {
    const error = new ApiError(0, null, "Couldn't reach the server. Check your connection.");
    error.cause = cause;
    return error;
  }

  get isRefusal(): boolean {
    return this.status === 409 || this.status === 422;
  }

  /** The first message for a field, matched case-insensitively (the API keys fields in PascalCase). */
  fieldError(field: string): string | undefined {
    const key = Object.keys(this.fieldErrors).find((k) => k.toLowerCase() === field.toLowerCase());
    return key ? this.fieldErrors[key]?.[0] : undefined;
  }
}

export const isApiError = (error: unknown): error is ApiError => error instanceof ApiError;

/** What a Banner should say under "Reason given:" for any thrown error. */
export function reasonOf(error: unknown): string | undefined {
  if (isApiError(error)) return error.reason ?? error.message;
  return error instanceof Error ? error.message : undefined;
}
